import { useMemo, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchForms, fetchSession, createForm, createTeam, updateFormMetadata } from "../lib/api-client";
import type { FormSummary, ListFormsResponse } from "../types/api";
import { CreateFormModal } from "../components/CreateFormModal";
import { CreateTeamModal } from "../components/CreateTeamModal";

// Simple Switch Component for this page
function Switch({ checked, onChange, disabled }: { checked: boolean; onChange: (checked: boolean) => void; disabled?: boolean }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) onChange(!checked);
      }}
      disabled={disabled}
      style={{
        position: "relative",
        display: "inline-block",
        width: "36px",
        height: "20px",
        backgroundColor: checked ? "var(--color-primary)" : "var(--color-border)",
        borderRadius: "20px",
        border: "none",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "background-color 0.2s",
        padding: 0,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <span
        style={{
          position: "absolute",
          top: "2px",
          left: checked ? "18px" : "2px", // 36 - 16 - 2 = 18
          width: "16px",
          height: "16px",
          backgroundColor: "#fff",
          borderRadius: "50%",
          transition: "left 0.2s",
          boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
        }}
      />
    </button>
  );
}

function groupByTeam(forms: FormSummary[]) {
  const map = new Map<string, { teamId: string; forms: FormSummary[] }>();
  for (const form of forms) {
    if (!map.has(form.teamId)) {
      map.set(form.teamId, { teamId: form.teamId, forms: [] });
    }
    map.get(form.teamId)!.forms.push(form);
  }
  return Array.from(map.values());
}

function getErrorMessage(error: unknown): string {
  if (!error) return "";
  if (typeof error === "object" && error !== null && "message" in error) {
    const message = (error as { message: string }).message;
    // Make error messages more user-friendly
    if (message.includes("UNIQUE constraint failed: teams.slug")) {
      return "This team slug is already taken. Please choose a different one.";
    }
    if (message.includes("UNIQUE constraint failed")) {
      return "This value is already in use. Please choose a different one.";
    }
    return message;
  }
  return "An unexpected error occurred. Please try again.";
}

function FormDirectoryPage(): JSX.Element {
  const navigate = useNavigate();
  const { teamId } = useParams<{ teamId: string }>();
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);

  const sessionQuery = useQuery({
    queryKey: ["session"],
    queryFn: fetchSession,
  });

  const formsQuery = useQuery({
    queryKey: ["forms"],
    queryFn: () => fetchForms(),
  });

  const memberships = sessionQuery.data?.session?.memberships ?? [];

  // Auto-redirect logic:
  // 1. If no teamId in URL, redirect to first team
  // 2. If teamId in URL but user doesn't have membership, redirect to first authorized team
  useEffect(() => {
    if (memberships.length === 0) return; // Wait until memberships are loaded

    if (!teamId) {
      // No team specified, redirect to first team
      navigate(`/teams/${memberships[0].teamId}`, { replace: true });
      return;
    }

    // Check if user has membership in the specified team
    const hasMembership = memberships.some((m) => m.teamId === teamId);
    if (!hasMembership) {
      // User doesn't have access to this team, redirect to their first team
      navigate(`/teams/${memberships[0].teamId}`, { replace: true });
    }
  }, [teamId, memberships, navigate]);

  const createFormMutation = useMutation({
    mutationFn: createForm,
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["forms"] });
      setShowCreateModal(false);
      setSelectedTeamId(null);
      // Navigate to the newly created form
      navigate(`/teams/${data.form.teamId}/forms/${data.form.id}`);
    },
  });

  const createTeamMutation = useMutation({
    mutationFn: createTeam,
    onSuccess: async () => {
      // Refetch session to get updated memberships
      await sessionQuery.refetch();
      await formsQuery.refetch();
      setShowCreateTeamModal(false);
    },
  });

  const updateFormMutation = useMutation({
    mutationFn: ({ formId, enabled }: { formId: string; enabled: boolean }) =>
      updateFormMetadata(formId, { settings: { enabled } }),
    onMutate: async ({ formId, enabled }) => {
      await queryClient.cancelQueries({ queryKey: ["forms"] });
      const previousForms = queryClient.getQueryData<ListFormsResponse>(["forms"]);

      if (previousForms) {
        queryClient.setQueryData<ListFormsResponse>(["forms"], {
          ...previousForms,
          items: previousForms.items.map((form: FormSummary) =>
            form.id === formId
              ? { ...form, settings: { ...form.settings, enabled } }
              : form
          ),
        });
      }
      return { previousForms };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousForms) {
        queryClient.setQueryData(["forms"], context.previousForms);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["forms"] });
    },
  });

  const grouped = useMemo(() => {
    if (!formsQuery.data?.items) return [];
    // Filter forms by the selected team if a teamId is present in the URL
    const items = teamId
      ? formsQuery.data.items.filter((form) => form.teamId === teamId)
      : formsQuery.data.items;
    return groupByTeam(items);
  }, [formsQuery.data, teamId]);



  const canCreateForm = (membership?: typeof memberships[0]) => {
    if (!membership) return false;
    // Check if user has permission to create forms
    return (
      membership.role === "TeamAdmin" ||
      membership.role === "FormEditor" ||
      membership.role === "SuperAdmin" ||
      membership.roles?.includes("TeamAdmin") ||
      membership.roles?.includes("FormEditor")
    );
  };

  // Only admins and editors can toggle status
  const canManageForm = (membership?: typeof memberships[0]) => canCreateForm(membership);

  const hasAnyCreatePermission = memberships.some((m) => canCreateForm(m));

  const handleCreateForm = (teamId: string) => {
    setSelectedTeamId(teamId);
    setShowCreateModal(true);
  };

  const handleToggleStatus = (formId: string, enabled: boolean) => {
    updateFormMutation.mutate({ formId, enabled });
  };

  const handleSubmitForm = (data: { teamId: string; title: string; description: string }) => {
    createFormMutation.mutate({
      teamId: data.teamId,
      title: data.title,
      description: data.description || null,
    });
  };

  const handleSubmitTeam = (data: { name: string; slug: string }) => {
    createTeamMutation.mutate({
      name: data.name,
      slug: data.slug || null,
      planTier: null,
    });
  };

  const getTeamName = (teamId: string) => {
    return memberships.find((m) => m.teamId === teamId)?.teamName ?? "Team";
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <header className="page-header">
        <div>
          <h1 style={{ fontSize: "1.5rem", marginBottom: "0.25rem" }}>Forms</h1>
          <p style={{ color: "var(--color-text-muted)", margin: 0 }}>
            Manage and organize your forms across teams.
          </p>
        </div>
        <button
          className="button primary"
          onClick={() => {
            const firstTeam = memberships.find((m) => canCreateForm(m));
            if (firstTeam) {
              handleCreateForm(firstTeam.teamId);
            }
          }}
          disabled={!hasAnyCreatePermission}
        >
          Create new form
        </button>
      </header>

      <main style={{ padding: "2rem", flex: 1 }}>
        {formsQuery.isLoading ? (
          <div style={{ color: "var(--color-text-muted)" }}>Loading forms...</div>
        ) : memberships.length === 0 ? (
          <div
            className="surface"
            style={{ padding: "3rem", textAlign: "center", maxWidth: 640, margin: "0 auto" }}
          >
            <h2 style={{ marginBottom: "0.5rem" }}>Welcome to rwaforms Builder</h2>
            <p style={{ color: "var(--color-text-muted)", marginBottom: "1.5rem" }}>
              To get started, you need to create a team first. Teams allow you to collaborate with others and organize your forms.
            </p>
            <button
              className="button primary"
              onClick={() => setShowCreateTeamModal(true)}
            >
              Create Your First Team
            </button>
          </div>
        ) : grouped.length === 0 ? (
          <div
            className="surface"
            style={{ padding: "3rem", textAlign: "center", maxWidth: 640, margin: "0 auto" }}
          >
            <h2 style={{ marginBottom: "0.5rem" }}>No forms yet</h2>
            <p style={{ color: "var(--color-text-muted)", marginBottom: "1.5rem" }}>
              We could not find any forms for your account. Create your first form to get started.
            </p>
            <button
              className="button primary"
              onClick={() => {
                const firstTeam = memberships.find((m) => canCreateForm(m));
                if (firstTeam) {
                  handleCreateForm(firstTeam.teamId);
                }
              }}
              disabled={!hasAnyCreatePermission}
            >
              Create Form
            </button>
          </div>
        ) : (
          <div style={{ display: "grid", gap: "1.5rem" }}>
            {grouped.map((group) => {
              const membership = memberships.find((m) => m.teamId === group.teamId);
              return (
                <section key={group.teamId} className="surface" style={{ padding: "1.5rem" }}>
                  <header className="section-header">
                    <div>
                      <h2 style={{ fontSize: "1.125rem" }}>
                        {membership?.teamName ?? "Team"} ({group.forms.length})
                      </h2>
                      <p style={{ color: "var(--color-text-muted)", margin: 0 }}>
                        Role: {membership?.role ?? "Viewer"}
                      </p>
                    </div>
                    <div className="section-header-actions">
                      <button
                        className="button ghost"
                        onClick={() => navigate(`/teams/${group.teamId}/settings`)}
                      >
                        Manage team
                      </button>
                      <button
                        className="button secondary"
                        onClick={() => handleCreateForm(group.teamId)}
                        disabled={!canCreateForm(membership)}
                      >
                        New form
                      </button>
                    </div>
                  </header>
                  <div className="form-cards-grid">
                    {group.forms.map((form) => {
                      // Default to enabled if not set, or check explicit false
                      // Assume undefined/null means enabled by default for UX unless we want strict opt-in
                      // But let's check existing data: usually it's open.
                      // Let's coerce to boolean, defaulting to true if undefined
                      const isEnabled = (form.settings.enabled as boolean) ?? true;

                      return (
                        <article
                          key={form.id}
                          className="surface"
                          style={{
                            padding: "1rem",
                            borderRadius: "12px",
                            border: "1px solid var(--color-border)",
                            cursor: "pointer",
                            transition: "border 0.15s ease, transform 0.15s ease",
                            opacity: isEnabled ? 1 : 0.7,
                          }}
                          onClick={() => navigate(`/teams/${form.teamId}/forms/${form.id}`)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              navigate(`/teams/${form.teamId}/forms/${form.id}`);
                            }
                          }}
                          tabIndex={0}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: "0.5rem" }}>
                            <div className="pill">
                              {form.status.toUpperCase()}
                            </div>

                            {canManageForm(membership) && (
                              <div
                                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                                title={isEnabled ? "Disable form" : "Enable form"}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Switch
                                  checked={isEnabled}
                                  onChange={(checked) => handleToggleStatus(form.id, checked)}
                                  disabled={updateFormMutation.isPending}
                                />
                              </div>
                            )}
                          </div>

                          <h3 style={{ fontSize: "1.05rem", marginBottom: "0.25rem" }}>
                            {form.title || "Untitled form"}
                          </h3>
                          <p
                            style={{
                              color: "var(--color-text-muted)",
                              fontSize: "0.9rem",
                              marginBottom: "0.75rem",
                            }}
                          >
                            {form.description ?? "No description"}
                          </p>
                          <footer
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              color: "var(--color-text-muted)",
                              fontSize: "0.78rem",
                            }}
                          >
                            <span>Updated {new Date(form.updatedAt).toLocaleDateString()}</span>
                            <span>v{form.publishedVersionNumber ?? 1}</span>
                          </footer>
                        </article>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </main>

      {showCreateModal && selectedTeamId && (
        <CreateFormModal
          teamId={selectedTeamId}
          teamName={getTeamName(selectedTeamId)}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedTeamId(null);
            createFormMutation.reset();
          }}
          onSubmit={handleSubmitForm}
          isLoading={createFormMutation.isPending}
          error={getErrorMessage(createFormMutation.error)}
        />
      )}

      {showCreateTeamModal && (
        <CreateTeamModal
          onClose={() => {
            setShowCreateTeamModal(false);
            createTeamMutation.reset();
          }}
          onSubmit={handleSubmitTeam}
          isLoading={createTeamMutation.isPending}
          error={getErrorMessage(createTeamMutation.error)}
        />
      )}
    </div>
  );
}

export default FormDirectoryPage;
